import React, { useCallback, useEffect } from 'react'
import ReactDOM from 'react-dom'
import Image from 'next/image'
import cx from 'classnames'
import styles from './Modal.module.scss'
import { ButtonProps, FormProps, ModalProps } from '~types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark as closeIcon } from '@fortawesome/free-solid-svg-icons'
import { disableBodyScroll, getPortalRoot, isComponentOfType } from 'utils'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { DynamicComponent } from '~elements'
import { useWindowSize } from 'utils/hooks'

const Modal = ({
  backgroundImageXl,
  backgroundImageSm,
  blocks,
  children,
  closeOnOutsideClick = false,
  form,
  handleModalClose,
  isOpen = false,
  type
}: ModalProps) => {
  useEffect(() => {
    disableBodyScroll(isOpen)
    return () => disableBodyScroll(false)
  }, [isOpen])
  const { isBreakpoint: isBelowSm } = useWindowSize('sm', '<')
  const modalBlocks = type === 'event_announcement' ? blocks : type === 'form' ? [form] : []
  const backgroundImage = isBelowSm ? backgroundImageSm ?? backgroundImageXl : backgroundImageXl

  // Handle Escape key to close modal
  const handleKeyEvent = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleModalClose()
      }
    },
    [isOpen, handleModalClose]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyEvent)
    return () => window.removeEventListener('keydown', handleKeyEvent)
  }, [handleKeyEvent])

  // Handle clicking outside modal
  const onModalOutsideClick = (e: React.MouseEvent<HTMLElement>) => {
    if (e.target === e.currentTarget && closeOnOutsideClick) {
      handleModalClose()
      disableBodyScroll(false)
    }
  }

  const containerClassNames = cx(styles['modal-container'], styles[`modal-container--type-${type}`])

  const modalContent = isOpen ? (
    <div
      className={styles['modal']}
      role='dialog'
      aria-modal='true'
      // @TODO: add aria-labelledby & aria-describedby
    >
      <div className={styles['modal-dialog']} role='document' onClick={onModalOutsideClick}>
        <div className={containerClassNames}>
          <FontAwesomeIcon icon={closeIcon} onClick={handleModalClose} />
          <div className={styles['modal-content']}>
            {modalBlocks && modalBlocks.length > 0
              ? modalBlocks.map(block => {
                  const isButtonComponent = isComponentOfType<ButtonProps>(block, 'Button')
                  const isFormComponent = isComponentOfType<FormProps>(block, 'Form')
                  const enhancedBlock = {
                    ...block,
                    ...(block &&
                      isFormComponent && {
                        isChildOfModal: true
                      }),
                    ...(block &&
                      isButtonComponent && {
                        analytics: {
                          linkLocation: 'modal',
                          linkModule: `modal_${type}`
                        },
                        ...(isBelowSm && {
                          isFullWidth: true
                        })
                      })
                  }
                  return <DynamicComponent key={block?.id} block={enhancedBlock} />
                })
              : children
              ? children
              : null}
          </div>
          {backgroundImage?.url && (
            <Image
              src={backgroundImage.url}
              alt={backgroundImage?.altText || ''}
              fill
              style={{ objectFit: 'cover' }}
              quality={NEXT_IMAGE_QUALITY}
            />
          )}
        </div>
      </div>
    </div>
  ) : null

  const portalRoot = getPortalRoot()
  if (!portalRoot) return null

  return ReactDOM.createPortal(modalContent, portalRoot)
}

export default Modal
